import { useState, useEffect } from 'react';
import { X, Clock, Loader2, ChevronRight } from 'lucide-react';

import { cn, formatTimeAgo, formatLamports } from '@/lib/utils';
import { useHistory } from '@/hooks/useHistory';
import type { HistoryItem } from '@/types/schemas';

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HistoryDrawer({ open, onClose }: HistoryDrawerProps) {
  const [cursor, setCursor] = useState<number | undefined>();
  const [allItems, setAllItems] = useState<HistoryItem[]>([]);

  const { data, isLoading, isFetching } = useHistory(cursor);

  // Handle data updates: replace on fresh load, append on pagination
  useEffect(() => {
    if (data) {
      if (cursor) {
        // Pagination: append new items
        setAllItems((prev) => [...prev, ...data.items]);
      } else {
        // Fresh load or refresh: replace items
        setAllItems(data.items);
      }
    }
  }, [data, cursor]);

  const handleLoadMore = () => {
    if (data?.next_cursor) {
      setCursor(data.next_cursor);
    }
  };

  const getActionIcon = (kind: string) => {
    const iconSize = 20;

    switch (kind) {
      case 'deposit':
        return '💰';
      case 'withdraw':
        return '💸';
      case 'burn_token':
        return <img src="/trash.svg" alt="Trash" className="w-5 h-5" />;
      case 'burn_nft':
        return '🖼️';
      case 'chest_open':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 30 30" fill="none">
            <path d="M27.9515 7.43694C27.9515 5.98682 27.3754 4.59608 26.35 3.57069C25.3246 2.5453 23.9339 1.96924 22.4838 1.96924H7.81432C6.3642 1.96924 4.97346 2.5453 3.94807 3.57069C2.92268 4.59608 2.34662 5.98682 2.34662 7.43694M27.9515 7.43694H2.34662M27.9515 7.43694L24.9509 14.2382M2.34662 7.43694L5.34719 14.2382M2.34662 14.2382H27.9515V28.1462H2.34662V14.2382ZM12.958 14.2382H17.3415C17.3468 14.2382 17.3518 14.2403 17.3556 14.2441C17.3594 14.2478 17.3615 14.2529 17.3615 14.2582V18.5057C17.3616 18.7963 17.3045 19.0841 17.1934 19.3526C17.0823 19.6211 16.9193 19.865 16.7138 20.0705C16.5084 20.276 16.2644 20.4389 15.9959 20.5501C15.7274 20.6612 15.4396 20.7183 15.149 20.7181C14.5626 20.7181 14.0002 20.4852 13.5856 20.0705C13.1709 19.6559 12.938 19.0935 12.938 18.507V14.2582C12.938 14.2529 12.9401 14.2478 12.9438 14.2441C12.9476 14.2403 12.9527 14.2382 12.958 14.2382Z" stroke="white" strokeWidth="3.30385" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'chest_reward':
        return '🎉';
      default:
        return '❓';
    }
  };

  const getActionLabel = (kind: string) => {
    switch (kind) {
      case 'deposit':
        return 'Deposited SOL';
      case 'withdraw':
        return 'Withdrew SOL';
      case 'burn_token':
        return 'Junk';
      case 'burn_nft':
        return 'Burned NFT';
      case 'chest_open':
        return 'Opened Chests';
      case 'chest_reward':
        return 'Chest Prize';
      default:
        return kind;
    }
  };

  const isCredit = (item: HistoryItem) => {
    // Handle null/undefined/empty values
    if (!item.sol_delta_lamports || item.sol_delta_lamports === '0') {
      // Burns should always be treated as credits (SOL earned)
      if (item.kind === 'burn_token' || item.kind === 'burn_nft') {
        return true;
      }
      return false;
    }

    try {
      const amount = BigInt(item.sol_delta_lamports);
      return amount > 0;
    } catch {
      console.warn('Invalid sol_delta_lamports value:', item.sol_delta_lamports);
      return false;
    }
  };

  const getAbsoluteAmount = (item: HistoryItem) => {
    // Handle null/undefined/empty values
    if (!item.sol_delta_lamports || item.sol_delta_lamports === '0') {
      return '0';
    }

    try {
      const amount = BigInt(item.sol_delta_lamports);
      return amount < 0 ? (-amount).toString() : amount.toString();
    } catch {
      console.warn('Invalid sol_delta_lamports value:', item.sol_delta_lamports);
      return '0';
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-96">
      {/* Backdrop (mobile only) */}
      <div className="lg:hidden absolute inset-0 bg-black/95 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 lg:inset-y-0 lg:right-0',
          'border-t lg:border-t-0 lg:border-l',
          'rounded-t-xl lg:rounded-none',
          'shadow-2xl',
          'max-h-[80vh] lg:max-h-none lg:h-full',
          'animate-slide-in',
          'flex flex-col'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#50E5F8',
          boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.3)' }}>
          <div className="flex items-center space-x-2">
            <Clock size={20} style={{ color: '#50E5F8' }} />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>SOL CREDITS HISTORY</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading && allItems.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 size={24} className="animate-spin" style={{ color: '#50E5F8' }} />
            </div>
          ) : allItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <Clock size={48} className="mb-4" style={{ color: '#50E5F8' }} />
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>NO HISTORY YET</h3>
              <p className="text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Start junking tokens or opening chests to see your SOL credit history
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {allItems.map((item) => {
                const credit = isCredit(item);
                const absAmount = getAbsoluteAmount(item);

                return (
                  <div
                    key={item.id}
                    className="p-4 transition-colors"
                    style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.2)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="text-xl flex items-center justify-center min-w-[24px]">{getActionIcon(item.kind)}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-sm" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                            {getActionLabel(item.kind)}
                          </div>
                          <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                            {formatTimeAgo(item.created_at)}
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div
                            className="text-sm font-semibold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              color: credit ? '#10B981' : '#EF4444'
                            }}
                          >
                            {credit ? '+' : '-'}
                            {formatLamports(absAmount)} SOL
                          </div>

                          {item.tx_hash && (
                            <a
                              href={`https://solscan.io/tx/${item.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs underline"
                              style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8' }}
                            >
                              View TX
                            </a>
                          )}
                        </div>
                      </div>

                      <ChevronRight size={16} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </div>
                  </div>
                );
              })}

              {/* Load More */}
              {data?.has_more && (
                <div className="p-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    className="w-full p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    style={{
                      fontFamily: 'Hyperspace Race Capsule, cursive',
                      backgroundColor: 'rgba(80, 228, 249, 0.1)',
                      color: '#50E5F8',
                      border: '1px solid rgba(80, 228, 249, 0.3)',
                    }}
                    onMouseEnter={(e) => !isFetching && (e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.2)')}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.1)'}
                  >
                    {isFetching ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>LOADING...</span>
                      </>
                    ) : (
                      <span>LOAD MORE</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }}>
          <button
            onClick={onClose}
            className="w-full p-2 rounded-lg text-sm transition-colors"
            style={{
              fontFamily: 'Hyperspace Race Capsule, cursive',
              backgroundColor: 'rgba(80, 228, 249, 0.1)',
              color: '#50E5F8',
              border: '1px solid rgba(80, 228, 249, 0.3)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.1)'}
          >
            CLOSE HISTORY
          </button>
        </div>
      </div>
    </div>
  );
}
